// Storyline. The LIFO stack of pending game events.
//
// Pure data + transitions. The renderer (GameBoard / StorylinePile)
// reads the stack and draws the visible pile when 1+ events pend;
// when empty, no UI shows.
//
// ── Codex anchors (Storyline) ──
//
//   "A storyline begins each time a spell is cast, or an ability is
//    activated or triggered."
//   "If the resolution of one event triggers a new event, the new
//    event will be inserted into the storyline just before the
//    triggering event." → LIFO. New events resolve first.
//   "If multiple events are competing to be added at the same point
//    on the storyline, the active player adds all of their events
//    first, in the order of their choice. Then the non-active
//    player adds their events in the order of their choice." → the
//    non-active player's events end up on TOP (resolve first).
//   "Replacement effects, prevention effects, and ongoing effects do
//    not use the storyline." → those bypass push entirely.
//   "Effects [that] use language like 'each' or 'all,' without also
//    using 'one at a time'" → ONE storyline event with multiple
//    operations (caller's responsibility to bundle).
//
// ── Stack convention (Array, internally) ──
//
//   events[0]                 → bottom of stack (resolves LAST)
//   events[events.length - 1] → top of stack    (resolves NEXT)
//
//   This matches the natural array push/pop semantics. The renderer
//   inverts the visual order so the top of stack appears at the
//   front of the pile (which is where the active confirm UI sits).
//
// ── Event shape ──
//
//   {
//     id:          string. Unique per event
//     kind:        'cast_spell' | 'activate_ability' | 'triggered_ability'
//                                   | 'placement_choice' | 'caster_pick'
//                                   | 'reminder' | ... (callers may extend)
//     sourceCardId: string. Instance id of the source card
//     sourceName:  string. Human-readable, e.g. "Bottomless Pit"
//     controller:  'p1' | 'p2'. Who owns the effect
//     addedBy:     'cast' | 'trigger' | 'activate' | 'place'
//     description?: string. Optional one-line summary for the UI
//     .... Additional caller-specific fields
//                                     (kept as-is; today these are the
//                                     same Prompt shape used by the
//                                     EffectPromptDrawer)
//   }
//
// Today's pendingPrompts array IS the storyline. The existing prompt
// objects already carry most of these fields; this module formalises
// the access patterns and the rules that govern push order.
//
// ── Known Phase-1 caveat (intentionally deferred) ──
//
// When a single card emits multiple effect entries that each become
// their own prompt (rare today. Most automation cards have one
// entry), the caller appends them to the stack in array order. With
// LIFO resolution this means the LAST entry resolves first. If a
// future card needs strict author-declared order, the caller should
// either:
//   (a) reverse the batch before pushing, or
//   (b) bundle the entries into a single storyline event with
//       multiple operations (the "simultaneous effects" codex
//       pattern. "each / all" without "one at a time").
// No current automation triggers this scenario; revisit on the first
// card that needs it.

/** Top of stack (next to resolve), or null if empty. */
export function getActiveEvent(events) {
  if (!Array.isArray(events) || events.length === 0) return null;
  return events[events.length - 1];
}

/** Every event under the active one, top-of-pile first (visual order). */
export function getPileEvents(events) {
  if (!Array.isArray(events) || events.length <= 1) return [];
  // Slice everything except the active (last) element, then reverse so
  // the most-recently-pushed pending event appears first in the
  // rendered pile (closest to the active card visually).
  return events.slice(0, -1).reverse();
}

/** True when the storyline has no events. The UI should hide entirely. */
export function isStorylineEmpty(events) {
  return !Array.isArray(events) || events.length === 0;
}

/**
 * Push a new event onto the top of the stack. Per codex:
 *   "If the resolution of one event triggers a new event, the new
 *    event will be inserted into the storyline just before the
 *    triggering event."
 * Since our convention puts top-of-stack at events[length-1], a
 * trigger fired during resolution just appends. This is also the
 * cast/activate path. No distinction.
 */
export function pushEvent(events, event) {
  if (!event) return events || [];
  return [...(events || []), event];
}

/**
 * Bundle multiple "simultaneous" events into the stack at once, with
 * codex active-player ordering:
 *
 *   "The active player adds all of their events first, in the order
 *    of their choice. Then, the non-active player adds their events
 *    in the order of their choice. The non-active player's events
 *    will end up resolving first, since newly added events always
 *    interrupt what's already on the storyline."
 *
 * Caller supplies each event's `controller` and the current active
 * player. We push active-player events first (deeper in the stack),
 * then non-active events (higher in the stack → resolve first).
 *
 * Within each player's group, original incoming order is preserved
 * The caller has already arranged "the order of their choice".
 */
export function pushSimultaneous(events, newEvents, activePlayer) {
  if (!Array.isArray(newEvents) || newEvents.length === 0) return events || [];
  const active   = newEvents.filter((e) => e.controller === activePlayer);
  const nonActive = newEvents.filter((e) => e.controller !== activePlayer);
  return [...(events || []), ...active, ...nonActive];
}

/** Remove an event by id (e.g. on confirm / cancel resolution). */
export function removeEventById(events, id) {
  if (!Array.isArray(events) || events.length === 0) return events || [];
  return events.filter((e) => e.id !== id);
}

/**
 * Drop any pending start-phase `draw_phase_choice` events owned by the
 * given controller. Called when the player passes their turn without
 * resolving the mandatory draw. The entry is forfeited rather than
 * carried into the next turn, where it would otherwise sit in the
 * storyline pile with no usable buttons (the StartDrawOverlay only
 * targets the CURRENT player's piles).
 */
export function dropDrawPhaseChoices(events, controller) {
  if (!Array.isArray(events) || events.length === 0) return events || [];
  return events.filter(
    (e) => !(e?.promptType === 'draw_phase_choice' && e.controller === controller),
  );
}

/** True iff `id` is the top of the stack. */
export function isActiveEvent(events, id) {
  const top = getActiveEvent(events);
  return !!top && top.id === id;
}
